import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, GitCompareArrows, ScanSearch, Save, X } from 'lucide-react';
import apiService from '../../services/api';
import { directionForLanguage } from '../../i18n/config';
import './AnalyzeAgainModal.css';
import './ClientRequestPreferencesModal.css';
import './AgentAutoApplyPreferencesModal.css';

interface AgentAutoApplyPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEnabled: boolean;
  onSaved?: (enabled: boolean) => void | Promise<void>;
}

export const AgentAutoApplyPreferencesModal: React.FC<AgentAutoApplyPreferencesModalProps> = ({
  isOpen,
  onClose,
  initialEnabled,
  onSaved,
}) => {
  const { t, i18n } = useTranslation('taskpane');
  const modalDir = directionForLanguage(i18n.language);
  const [enabled, setEnabled] = React.useState<boolean>(initialEnabled);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setEnabled(initialEnabled);
      setError(null);
    }
  }, [isOpen, initialEnabled]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await apiService.updateAgentAutoApplyPreference(enabled);
      if (onSaved) await onSaved(enabled);
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail || e?.message || t('profile.agentAutoApplyModal.saveFailed'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="aa-modal-backdrop cr-prefs-modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSaving) onClose();
      }}
    >
      <div
        className="aa-modal cr-prefs-modal agent-auto-modal"
        dir={modalDir}
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-auto-modal-title"
      >
        <div className="aa-modal-header">
          <div className="aa-modal-header-text">
            <h2 id="agent-auto-modal-title" className="aa-modal-title">
              {t('profile.agentAutoApplyModal.title')}
            </h2>
            <p className="aa-modal-subtitle">{t('profile.agentAutoApplyModal.subtitle')}</p>
          </div>
          <button
            type="button"
            className="aa-modal-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label={t('actions.close', { ns: 'common' })}
          >
            <X size={18} />
          </button>
        </div>

        <p className="aa-modal-lead">{t('profile.agentAutoApplyModal.lead')}</p>

        {error && <div className="cr-prefs-error">{error}</div>}

        <label className="agent-auto-toggle-row">
          <span className="agent-auto-toggle-copy">
            <Bot size={16} className="cr-prefs-group-icon" />
            <span className="agent-auto-toggle-label">
              {t('profile.agentAutoApplyModal.enable')}
            </span>
          </span>
          <span className="agent-auto-switch">
            <input
              type="checkbox"
              checked={enabled}
              disabled={isSaving}
              onChange={(e) => setEnabled(e.target.checked)}
              aria-label={t('profile.agentAutoApplyModal.enable')}
            />
            <span className="agent-auto-switch-track" aria-hidden />
          </span>
        </label>

        <div className="agent-auto-modes">
          <div className="agent-auto-mode">
            <GitCompareArrows size={15} className="cr-prefs-group-icon" />
            <span className="agent-auto-mode-copy">
              <span className="agent-auto-mode-title">
                {t('initiation.reviewModes.review_and_respond_redlines.label', { ns: 'mcc' })}
              </span>
              <span className="agent-auto-mode-desc">
                {t('profile.agentAutoApplyModal.redlinesDesc')}
              </span>
            </span>
          </div>
          <div className="agent-auto-mode">
            <ScanSearch size={15} className="cr-prefs-group-icon" />
            <span className="agent-auto-mode-copy">
              <span className="agent-auto-mode-title">
                {t('initiation.reviewModes.first_pass_legal_review.label', { ns: 'mcc' })}
              </span>
              <span className="agent-auto-mode-desc">
                {t('profile.agentAutoApplyModal.firstPassDesc')}
              </span>
            </span>
          </div>
        </div>

        <div className="aa-modal-actions cr-prefs-actions">
          <button
            type="button"
            className="aa-btn-secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            {t('actions.cancel', { ns: 'common' })}
          </button>
          <button
            type="button"
            className="aa-btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            <Save size={14} aria-hidden />
            {isSaving ? t('status.saving', { ns: 'common' }) : t('actions.save', { ns: 'common' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentAutoApplyPreferencesModal;
